import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Academy, Club, Organization } from '@court-sync/shared';
import { publicTenantsService } from '@/services/publicTenants';
import { getTenantSubdomainContext, type TenantSubdomainContext } from '@/lib/tenantHost';

interface OrgHostContextValue {
  host: TenantSubdomainContext;
  organization: Organization | null;
  academy: Academy | null;
  club: Club | null;
  /** Canonical org slug for APIs and path-mode links. */
  pathSlug: string | undefined;
  /** Legacy academy slug when academy module is present. */
  academySlug: string | undefined;
  /** Legacy club slug when club module is present. */
  clubSlug: string | undefined;
  loading: boolean;
  error: string | null;
}

const Ctx = createContext<OrgHostContextValue | null>(null);

export function OrgHostProvider({
  children,
  hostContext,
}: {
  children: React.ReactNode;
  hostContext?: TenantSubdomainContext;
}) {
  const host = hostContext ?? getTenantSubdomainContext();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [academy, setAcademy] = useState<Academy | null>(null);
  const [club, setClub] = useState<Club | null>(null);
  const [loading, setLoading] = useState(
    () => host.mode === 'subdomain' && Boolean(host.subdomainLabel)
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (host.mode !== 'subdomain' || !host.subdomainLabel) {
      return undefined;
    }

    let mounted = true;
    void Promise.resolve().then(() => {
      setLoading(true);
      setError(null);
    });

    publicTenantsService
      .getBySubdomain(host.subdomainLabel!)
      .then((res) => {
        if (!mounted) return;
        if (!res.success || !res.data) {
          setOrganization(null);
          setAcademy(null);
          setClub(null);
          setError(res.error || 'Organization not found');
          setLoading(false);
          return;
        }
        setOrganization(res.data.organization);
        setAcademy(res.data.academy ?? null);
        setClub(res.data.club ?? null);
        setError(null);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (mounted) {
          setOrganization(null);
          setAcademy(null);
          setClub(null);
          setError(e.message || 'Organization not found');
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [host.mode, host.subdomainLabel]);

  const value = useMemo(
    () => ({
      host,
      organization,
      academy,
      club,
      pathSlug: organization?.slug,
      academySlug: academy?.slug,
      clubSlug: club?.slug,
      loading,
      error,
    }),
    [host, organization, academy, club, loading, error]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOrgHost(): OrgHostContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    return {
      host: getTenantSubdomainContext(),
      organization: null,
      academy: null,
      club: null,
      pathSlug: undefined,
      academySlug: undefined,
      clubSlug: undefined,
      loading: false,
      error: null,
    };
  }
  return ctx;
}
